// The focus chips (design spec §8.1, workspace-modes note §6): scope · subject ✕ · week. Chips
// are 28 px (44 on coarse pointers), 12 px / 500, `--secondary`, radius 6. They only draw; the
// app owns the focus state.
import { ChevronDownIcon, XIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../cn";
import { Button } from "./button";
import { FOCUS_RING } from "./focus-ring";

const CHIP =
  "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm bg-secondary px-2.5 text-xs font-medium whitespace-nowrap text-secondary-foreground pointer-coarse:h-11";

type Option = { value: string; label: string };

function ChipSelect({
  value,
  options,
  onChange,
  className,
  ...props
}: Omit<React.ComponentProps<"select">, "onChange" | "value"> & {
  value: string;
  options: readonly Option[];
  onChange: (value: string) => void;
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          CHIP,
          "cursor-pointer appearance-none pr-7 transition-colors duration-(--dur-fast) hover:bg-active",
          FOCUS_RING,
          className,
        )}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2 text-faint"
      />
    </span>
  );
}

/** "Scope: Company"; a picker once there is more than one scope to pick. */
function ScopeChip({
  value,
  options,
  onChange,
  className,
}: {
  value: string;
  options: readonly Option[];
  onChange?: (value: string) => void;
  className?: string;
}) {
  const current = options.find((o) => o.value === value)?.label ?? value;
  if (options.length > 1 && onChange) {
    return (
      <ChipSelect
        aria-label="Scope"
        data-slot="focus-scope"
        value={value}
        options={options}
        onChange={onChange}
        className={className}
      />
    );
  }
  // A scope with nothing to pick says the obvious; phones drop it to keep the week in view.
  return (
    <span data-slot="focus-scope" className={cn(CHIP, "max-sm:hidden", className)}>
      Scope: {current}
    </span>
  );
}

/** The focused subject ("DE: Riley") with its own clear button (44 px on coarse pointers). */
function SubjectChip({
  kind,
  name,
  title,
  clearLabel,
  onClear,
  className,
}: {
  kind: string;
  name: string;
  /** Full identity for the tooltip (the chip truncates). */
  title?: string;
  clearLabel: string;
  onClear: () => void;
  className?: string;
}) {
  return (
    <span data-slot="subject-chip" className="inline-flex shrink-0 items-center gap-0.5">
      <span
        data-slot="focus-subject"
        title={title}
        className={cn(CHIP, "max-w-36 bg-primary-soft text-primary-text md:max-w-56", className)}
      >
        <span className="truncate">
          {kind}: {name}
        </span>
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="pointer-coarse:size-11"
        aria-label={clearLabel}
        onClick={onClear}
      >
        <XIcon aria-hidden="true" />
      </Button>
    </span>
  );
}

/** The week (`p`) picker; values are week keys, labels say the dates. */
function PeriodPicker({
  value,
  weeks,
  onChange,
  className,
  "aria-label": label = "Week",
}: {
  value: string;
  weeks: readonly Option[];
  onChange: (value: string) => void;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <ChipSelect
      aria-label={label}
      data-slot="period-picker"
      value={value}
      options={weeks}
      onChange={onChange}
      className={className}
    />
  );
}

export { PeriodPicker, ScopeChip, SubjectChip };
